import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ReplacementReasonService } from '@app/features/contracts/replacement-reason-service';

@Component({
  selector: 'app-select-replacement-reason',
  imports: [CommonModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './select-replacement-reason.html',
  styleUrl: './select-replacement-reason.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectReplacementReason),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectReplacementReason),
      multi: true,
    },
  ],
})
export class SelectReplacementReason implements OnInit {
  private injector = inject(Injector);

  @Input() label = 'Motif de remplacement';
  @Input() value: string | undefined;
  @Input() required = false;
  @Output() selectionChange = new EventEmitter<string>();
  readonly replacementReasonsService = inject(ReplacementReasonService);
  private ngControl?: NgControl;
  disabled = false;

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    if (this.ngControl) {
      this.required = this.hasRequiredValidator(this.ngControl.control!);
      try {
        this.ngControl.valueAccessor = this;
      } catch {
        console.warn(
          'Impossible de définir le valueAccessor. Assurez-vous que ce composant est utilisé dans un contexte de formulaire (FormGroup, FormControl, etc.)',
        );
      }
    }
  }

  onChange: (value: string | undefined) => void = () => {
    // No-op
  };
  onTouched: () => void = () => {
    // No-op
  };

  writeValue(value: string | undefined): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const ctrl = control ?? this.control;
    const isRequired = this.required || (ctrl ? this.hasRequiredValidator(ctrl) : false);

    if (isRequired && !this.value) {
      return { required: true };
    }

    return null;
  }

  private hasRequiredValidator(control: AbstractControl): boolean {
    if (!control || !control.validator) return false;

    const validator = control.validator({} as AbstractControl);
    return !!(validator && validator['required']);
  }

  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }

  onSelectionChange(value: string): void {
    this.value = value;
    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(value);
  }
}
